import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Modal, Box, TextField, Button as MuiButton, Typography, Snackbar, Alert } from '@mui/material';
import { Table, Button as BootstrapButton, Form } from 'react-bootstrap';
import ENDPOINT from '../../context/EndPoint';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  maxHeight: '90vh',         // Add this line
  overflowY: 'auto',         // Add this line
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const ToolManager = () => {
  const [tools, setTools] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [openAdd, setOpenAdd] = useState(false);
  const [openUpdate, setOpenUpdate] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  // Form data
  const [addForm, setAddForm] = useState({
    tool_name: '',
    tool_description: '',
    tool_price: '',
    tool_rating: '',
    ratings: '',
    youtube_video_link: '',
    video_title: '',
    status: '',
    tool_image:''
  });
  const [updateForm, setUpdateForm] = useState({
    tool_id: '',
    tool_name: '',
    tool_description: '',
    tool_price: '',
    tool_rating: '',
    ratings: '',
    youtube_video_link: '',
    video_title: '',
    status: '',
    tool_image:''

  });
  const [deleteId, setDeleteId] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const toolNameRef = useRef(null);

  useEffect(() => {
    if (openAdd && toolNameRef.current) {
      // Focus the input element inside the TextField
      if (typeof toolNameRef.current.focus === 'function') {
        toolNameRef.current.focus();
      } else if (
        toolNameRef.current.querySelector &&
        toolNameRef.current.querySelector('input')
      ) {
        toolNameRef.current.querySelector('input').focus();
      }
    }
  }, [openAdd]);

  // Helper to show snackbar
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // Fetch tools (no search param)
  useEffect(() => {
    const fetchTools = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${ENDPOINT}get_sell_tools.php`);
        if (response.data.status === 'success') {
          setTools(response.data.data);
        } else {
          setError(response.data.message);
        }
      } catch (err) {
        setError('Failed to fetch tools');
      } finally {
        setLoading(false);
      }
    };

    fetchTools();
  }, []);

  // Local search filter
  const filteredTools = tools.filter(
    (tool) =>
      tool.tool_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.tool_description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle add tool
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.keys(addForm).forEach((key) => formData.append(key, addForm[key]));
      const response = await axios.post(`${ENDPOINT}create_sell_tool.php`, formData);
      if (response.data.status === 'success') {
        setTools([...tools, { tool_id: response.data.data.id, ...addForm }]);
        setAddForm({
          tool_name: '',
          tool_description: '',
          tool_price: '',
          tool_rating: '',
          ratings: '',
          youtube_video_link: '',
          video_title: '',
          status: '',
    tool_image:''

        });
        setOpenAdd(false);
        showSnackbar(response.data.message || 'Tool added successfully', 'success');
      } else {
        setError(response.data.message);
        showSnackbar(response.data.message, 'error');
      }
    } catch (err) {
      setError('Failed to create tool');
      showSnackbar('Failed to create tool', 'error');
    }
  };

  // Handle update tool
  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.keys(updateForm).forEach((key) => formData.append(key, updateForm[key]));
      const response = await axios.post(`${ENDPOINT}update_sell_tool.php`, formData);
      if (response.data.status === 'success') {
        setTools(tools.map((tool) => (tool.tool_id === updateForm.tool_id ? { ...updateForm } : tool)));
        setUpdateForm({
          tool_id: '',
          tool_name: '',
          tool_description: '',
          tool_price: '',
          tool_rating: '',
          ratings: '',
          youtube_video_link: '',
          video_title: '',
          status: '',
    tool_image:''

        });
        setOpenUpdate(false);
        showSnackbar(response.data.message || 'Tool updated successfully', 'success');
      } else {
        setError(response.data.message);
        showSnackbar(response.data.message, 'error');
      }
    } catch (err) {
      setError('Failed to update tool');
      showSnackbar('Failed to update tool', 'error');
    }
  };

  // Handle delete tool
  const handleDeleteSubmit = async () => {
    try {
      const formData = new FormData();
      formData.append('tool_id', deleteId);
      const response = await axios.post(`${ENDPOINT}delete_sell_tool.php`, formData);
      if (response.data.status === 'success') {
        setTools(tools.filter((tool) => tool.tool_id !== deleteId));
        setOpenDelete(false);
        setDeleteId(null);
        showSnackbar(response.data.message || 'Tool deleted successfully', 'success');
      } else {
        setError(response.data.message);
        showSnackbar(response.data.message, 'error');
      }
    } catch (err) {
      setError('Failed to delete tool');
      showSnackbar('Failed to delete tool', 'error');
    }
  };

  // Open update modal with tool data
  const openUpdateModal = (tool) => {
    setUpdateForm(tool);
    setOpenUpdate(true);
  };

  // Open delete modal
  const openDeleteModal = (tool_id) => {
    setDeleteId(tool_id);
    setOpenDelete(true);
  };

  if (loading) {
    return <div>Loading tools...</div>;
  }

  return (
    <div className="container-fluid mt-4">
      <h2 className="mb-3">Tool Manager</h2>
      <p className="text-muted mb-4">Manage tools for sale</p>

      {/* Search Bar */}
      <Form.Group className="mb-4">
        <Form.Control
          type="text"
          placeholder="Search by tool name or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Form.Group>

      {/* Add Tool Button */}
      <BootstrapButton variant="primary" className="mb-4" onClick={() => setOpenAdd(true)}>
        Add Tool
      </BootstrapButton>

      {/* Tools Table */}
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Description</th>
            <th>Price</th>
            <th>Rating</th>
            <th>Ratings</th>
            <th>YouTube Link</th>
            <th>Video Title</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredTools.map((tool) => (
            <tr key={tool.tool_id}>
              <td>{tool.tool_id}</td>
              <td>{tool.tool_name}</td>
              <td>{tool.tool_description}</td>
              <td>${parseFloat(+tool.tool_price).toFixed(2)}</td>
              <td>{parseFloat(+tool.tool_rating).toFixed(1)}</td>
              <td>{tool.ratings}</td>
              <td>
                <a href={tool.youtube_video_link} target="_blank" rel="noopener noreferrer">
                  Link
                </a>
              </td>
              <td>{tool.video_title}</td>
              <td>{tool.status}</td>
              <td>
                <div className="d-flex justify-content-between my-auto">
                  <EditIcon className="me-2 text-primary" onClick={() => openUpdateModal(tool)} type="button" />
                  <DeleteIcon className=" text-danger" onClick={() => openDeleteModal(tool.tool_id)} type="button" />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Add Tool Modal */}
      <Modal open={openAdd} onClose={() => setOpenAdd(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2" mb={3}>
            Add Tool
          </Typography>
          <form onSubmit={handleAddSubmit}>
            <TextField
              fullWidth
              inputRef={toolNameRef}
              label="Tool Name"
              name="tool_name"
              value={addForm.tool_name}
              onChange={(e) => setAddForm({ ...addForm, tool_name: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Description"
              name="tool_description"
              value={addForm.tool_description}
              onChange={(e) => setAddForm({ ...addForm, tool_description: e.target.value })}
              margin="normal"
              multiline
              rows={4}
              required
            />
            <TextField
              fullWidth
              label="Price"
              name="tool_price"
              type="number"
              step="0.01"
              value={addForm.tool_price}
              onChange={(e) => setAddForm({ ...addForm, tool_price: e.target.value })}
              margin="normal"
              required
            />
            <div className="d-flex gap-2">
              <TextField
                fullWidth
                label="Rating"
                name="tool_rating"
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={addForm.tool_rating}
                onChange={(e) => setAddForm({ ...addForm, tool_rating: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Number of Ratings"
                name="ratings"
                type="number"
                value={addForm.ratings}
                onChange={(e) => setAddForm({ ...addForm, ratings: e.target.value })}
                margin="normal"
                required
              />
            </div>
            <TextField
              fullWidth
              label="YouTube Video Link"
              name="youtube_video_link"
              value={addForm.youtube_video_link}
              onChange={(e) => setAddForm({ ...addForm, youtube_video_link: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Video Title"
              name="video_title"
              value={addForm.video_title}
              onChange={(e) => setAddForm({ ...addForm, video_title: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Image"
              name="tool_image"
              value={addForm.tool_image}
              onChange={(e) => setAddForm({ ...addForm, tool_image: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Status"
              name="status"
              value={addForm.status}
              onChange={(e) => setAddForm({ ...addForm, status: e.target.value })}
              margin="normal"
              required
            />
            <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
              <MuiButton onClick={() => setOpenAdd(false)}>Cancel</MuiButton>
              <MuiButton type="submit" variant="contained" color="primary">
                Add
              </MuiButton>
            </Box>
          </form>
        </Box>
      </Modal>

      {/* Update Tool Modal */}
      <Modal open={openUpdate} onClose={() => setOpenUpdate(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2" mb={3}>
            Update Tool
          </Typography>
          <form onSubmit={handleUpdateSubmit}>
            <TextField
              fullWidth
              label="Tool Name"
              name="tool_name"
              value={updateForm.tool_name}
              onChange={(e) => setUpdateForm({ ...updateForm, tool_name: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Description"
              name="tool_description"
              value={updateForm.tool_description}
              onChange={(e) => setUpdateForm({ ...updateForm, tool_description: e.target.value })}
              margin="normal"
              multiline
              rows={4}
              required
            />
            <TextField
              fullWidth
              label="Price"
              name="tool_price"
              type="number"
              step="0.01"
              value={updateForm.tool_price}
              onChange={(e) => setUpdateForm({ ...updateForm, tool_price: e.target.value })}
              margin="normal"
              required
            />
            <div className="d-flex gap-2">
              <TextField
                fullWidth
                label="Rating"
                name="tool_rating"
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={updateForm.tool_rating}
                onChange={(e) => setUpdateForm({ ...updateForm, tool_rating: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Number of Ratings"
                name="ratings"
                type="number"
                value={updateForm.ratings}
                onChange={(e) => setUpdateForm({ ...updateForm, ratings: e.target.value })}
                margin="normal"
                required
              />
            </div>
            <TextField
              fullWidth
              label="YouTube Video Link"
              name="youtube_video_link"
              value={updateForm.youtube_video_link}
              onChange={(e) => setUpdateForm({ ...updateForm, youtube_video_link: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Video Title"
              name="video_title"
              value={updateForm.video_title}
              onChange={(e) => setUpdateForm({ ...updateForm, video_title: e.target.value })}
              margin="normal"
              required
            />
               <TextField
              fullWidth
              label="Image"
              name="tool_image"
              value={updateForm.tool_image}
              onChange={(e) => setUpdateForm({ ...updateForm, tool_image: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Status"
              name="status"
              value={updateForm.status}
              onChange={(e) => setUpdateForm({ ...updateForm, status: e.target.value })}
              margin="normal"
              required
            />
            <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
              <MuiButton onClick={() => setOpenUpdate(false)}>Cancel</MuiButton>
              <MuiButton type="submit" variant="contained" color="primary">
                Update
              </MuiButton>
            </Box>
          </form>
        </Box>
      </Modal>

      {/* Delete Tool Modal */}
      <Modal open={openDelete} onClose={() => setOpenDelete(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2" mb={3}>
            Confirm Delete
          </Typography>
          <Typography mb={3}>
            Are you sure you want to delete tool ID {deleteId}?
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <MuiButton onClick={() => setOpenDelete(false)}>Cancel</MuiButton>
            <MuiButton variant="contained" color="error" onClick={handleDeleteSubmit}>
              Delete
            </MuiButton>
          </Box>
        </Box>
      </Modal>

      {/* Snackbar for API messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ToolManager;